import { LABELS_CONFIG } from '@/Hooks/PUJ/constants';
import Preloader from '@/Layouts/containers/Preloader';
import getLabel from '@/Utils/LabelsConfig';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import useMedia from '@/Hooks/useMedia';
import { ReactComponent as DeviceIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_device_box.svg';
import { ReactComponent as DesktopIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_device_desktop.svg';
import { ReactComponent as MobileIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_device_mobile.svg';
import { ReactComponent as TabletIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_device_tablet.svg';
import { ReactComponent as UnknownIcon } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_manage_devices.svg';
import { ReactComponent as WarningCircle } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_warning_circle.svg';
import Icon from '@ant-design/icons';
import {
  DeviceGeolocation,
  DeviceSession,
  DeviceType,
} from '@skytvnz/sky-app-store/lib/types/graph-ql';
import useDeviceSessions from '@/Hooks/useDeviceSessions';
import { PageViewedAnalyticsProps } from '@/Analytics/Page';
import { isNil } from 'ramda';
import styles from './styles.module.scss';
import SignOut, { Position } from '../SignOut';

const formatLocation = (location: DeviceGeolocation) => {
  if (!location) return <></>;
  return (
    <>
      {' '}
      &bull; {location.city ?? 'Unknown location'}, {location.countryCode}
    </>
  );
};

const getIcon = (deviceType: DeviceType) => {
  let icon;
  switch (deviceType) {
    case DeviceType.COMPUTER:
      icon = DesktopIcon;
      break;
    case DeviceType.PHONE:
      icon = MobileIcon;
      break;
    case DeviceType.TABLET:
      icon = TabletIcon;
      break;
    case DeviceType.BOX:
    case DeviceType.POD:
    case DeviceType.TV:
      icon = DeviceIcon;
      break;
    case DeviceType.UNKNOWN:
      icon = UnknownIcon;
      break;
    default:
      break;
  }
  return icon ? <Icon component={icon as React.FC} /> : <></>;
};

const DeviceItem = ({ device, index, labelCurrentSession }) => {
  return (
    <div key={`device-${device.id}`}>
      {index !== 0 && <div className={styles.divider} />}
      <div className={styles.deviceContainer}>
        <div className={styles.deviceIcon}>{getIcon(device.deviceType)}</div>
        <div className={styles.deviceInfo}>
          <div className={styles.deviceName}>{device.deviceDescription}</div>
          <div className={styles.deviceSubInfo}>
            {device.isCurrent && (
              <div className={styles.deviceCurrentSession}>{labelCurrentSession}</div>
            )}
            <div className={styles.deviceDescription}>
              {dayjs(device.lastSeen).format('MMM D, h:mm a')}
              {formatLocation(device.geolocation)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface Props {
  segmentPayload?: PageViewedAnalyticsProps;
}

const ManageDevices: React.FC<Props> = ({ segmentPayload }) => {
  const { isMediaM } = useMedia();
  const { deviceSessionsData, deviceSessionsIsLoading, deviceSessionsError } = useDeviceSessions();
  const [allDevices, setAllDevices] = useState<DeviceSession[]>([]);
  const labelSubheader = getLabel(LABELS_CONFIG.MANAGE_DEVICE_SUBHEADER);
  const labelNote = getLabel(LABELS_CONFIG.MANAGE_DEVICE_NOTE);
  const labelDescription = getLabel(LABELS_CONFIG.MANAGE_DEVICE_DESCRIPTION);
  const labelYourDevices = getLabel(LABELS_CONFIG.MANAGE_DEVICE_YOUR_DEVICES);
  const labelCurrentSession = getLabel(LABELS_CONFIG.MANAGE_DEVICE_CURRENT_SESSION);
  const labelNoDeviceHeader = getLabel(LABELS_CONFIG.MANAGE_DEVICE_NO_DEVICE_HEADER);
  const labelNoDeviceDescription = getLabel(LABELS_CONFIG.MANAGE_DEVICE_NO_DEVICE_DESCRIPTION);

  const isApiLoading = deviceSessionsData?.length === 0 && deviceSessionsIsLoading;
  const isApiError = deviceSessionsData?.length === 0 && !isNil(deviceSessionsError);

  useEffect(() => {
    if (deviceSessionsData?.length === 0) return;
    const currentDeviceData = deviceSessionsData?.filter(session => session.isCurrent) ?? [];
    const otherDevicesData = deviceSessionsData?.filter(session => !session.isCurrent) ?? [];
    setAllDevices([...currentDeviceData, ...otherDevicesData]);
  }, [deviceSessionsData]);

  return (
    <Preloader isLoading={!!isApiLoading}>
      <div className={styles.pageWrapper}>
        <div className={styles.pageTitle}>{labelSubheader}</div>
        <div className={styles.pageDescription}>
          {labelDescription}{' '}
          <a
            className={styles.link}
            href="https://account.sky.co.nz/account"
            target="_blank"
            rel="noopener noreferrer"
          >
            change your password
          </a>
          .
        </div>
        <div className={styles.pageNote}>{labelNote}</div>

        {allDevices.length > 0 && (
          <div className={styles.allDevicesContainer}>
            <div className={styles.sectionContainer}>
              <div className={styles.sectionTitle}>
                {String(labelYourDevices).replace('{count}', `(${allDevices.length.toString()})`)}
              </div>
              {isMediaM && <SignOut position={Position.Top} segmentPayload={segmentPayload} />}
            </div>

            <div className={styles.devicesListContainer}>
              {allDevices.map((device, index) => (
                <DeviceItem
                  device={device}
                  index={index}
                  labelCurrentSession={labelCurrentSession}
                />
              ))}
            </div>

            {!isMediaM && <SignOut position={Position.Bottom} segmentPayload={segmentPayload} />}
          </div>
        )}

        {isApiError && (
          <div className={styles.noDevicesContainer}>
            <div className={styles.noDevicesIcon}>
              <Icon component={WarningCircle as React.FC} />
            </div>
            <div className={styles.noDevicesHeader}>{labelNoDeviceHeader}</div>
            <div className={styles.noDevicesBody}>{labelNoDeviceDescription}</div>
          </div>
        )}
      </div>
    </Preloader>
  );
};

export default ManageDevices;
